import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/motion";
import { PageHero, CtaSection, SectionLabel, BtnGhost } from "../components/common";
import { PRODUCTS, PRODUCT_CATEGORY_DEFS, CONSUMABLES } from "../data/site";

const ProductCard = ({ p, i }) => (
  <Reveal delay={(i % 3) * 0.08}>
    <Link
      to={`/products/${p.slug}`}
      data-testid={`product-card-${p.slug}`}
      className="group block bg-white border border-slate-200 hover:border-brand-800 transition-colors h-full"
    >
      <div className="overflow-hidden bg-gradient-to-b from-slate-900 to-slate-800">
        <img src={p.image} alt={`${p.name} ${p.line}`} loading="lazy" className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105" />
      </div>
      <div className="p-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
          <span className="text-brand-600 font-semibold">{p.line}</span>
          <span className="text-slate-300">/</span>
          <span className="text-slate-500">{p.doorType}</span>
        </div>
        <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight text-brand-950 leading-snug">{p.name}</h3>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.tagline}</p>
        <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-800 group-hover:gap-2.5 transition-all">
          View Product <ArrowUpRight size={14} />
        </span>
      </div>
    </Link>
  </Reveal>
);

const ConsumableCard = ({ c, i }) => (
  <Reveal delay={(i % 3) * 0.08}>
    <div data-testid={`consumable-card-${c.slug}`} className="group bg-white border border-slate-200 hover:border-brand-800 transition-colors h-full flex flex-col">
      <div className="overflow-hidden bg-gradient-to-b from-slate-900 to-slate-800">
        <img src={c.image} alt={c.name} loading="lazy" className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105" />
      </div>
      <div className="p-8 flex flex-col flex-1">
        <div className="text-xs uppercase tracking-widest text-brand-600 font-semibold">Consumables</div>
        <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight text-brand-950 leading-snug">{c.name}</h3>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{c.description}</p>
        <Link to="/contact" data-testid={`consumable-cta-${c.slug}`} className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-800 hover:gap-2.5 transition-all">
          Request Information <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  </Reveal>
);

const ComingSoon = ({ name }) => (
  <div data-testid="products-coming-soon" className="border border-slate-200 bg-brand-50/40 p-16 text-center">
    <p className="font-heading text-2xl font-semibold text-brand-950">Products coming soon</p>
    <p className="mt-3 text-slate-600 max-w-md mx-auto">Our <span className="font-semibold text-brand-800">{name}</span> range is being finalised. Contact our team for current availability and specifications.</p>
    <div className="mt-8 flex justify-center"><BtnGhost to="/contact" testid="coming-soon-cta">Contact Our Team</BtnGhost></div>
  </div>
);

function CategorySection({ def }) {
  const models = PRODUCTS.filter((p) => p.line === def.name);
  const count = def.kind === "consumables" ? CONSUMABLES.length : models.length;
  return (
    <div id={def.slug} data-testid={`product-line-${def.slug}`} className="scroll-mt-28">
      <div className="flex items-end justify-between gap-6 border-b border-slate-200 pb-6">
        <div>
          <SectionLabel>Product Line</SectionLabel>
          <h2 className="mt-4 font-heading text-3xl md:text-5xl font-bold tracking-tighter text-brand-950">{def.name}</h2>
        </div>
        {count > 0 && (
          <span className="hidden md:block font-mono text-sm text-slate-400">{count} {def.kind === "consumables" ? "items" : "models"}</span>
        )}
      </div>

      {def.kind === "coming" && <div className="mt-10"><ComingSoon name={def.name} /></div>}

      {def.kind === "consumables" && (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CONSUMABLES.map((c, i) => <ConsumableCard key={c.slug} c={c} i={i} />)}
        </div>
      )}

      {def.kind === "models" && [...new Set(models.map((p) => p.doorType))].map((door) => {
        const dm = models.filter((p) => p.doorType === door);
        if (dm.length === 0) return null;
        return (
          <div key={door} className="mt-12" data-testid={`door-group-${def.slug}-${door.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-6 bg-brand-800" />
              <h3 className="font-heading text-lg font-semibold uppercase tracking-widest text-brand-800">{door}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dm.map((p, i) => <ProductCard key={p.slug} p={p} i={i} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Products() {
  const [params, setParams] = useSearchParams();
  const paramSlug = params.get("category");
  const activeDef = PRODUCT_CATEGORY_DEFS.find((d) => d.slug === paramSlug);
  const [active, setActive] = useState(activeDef ? activeDef.slug : "all");

  useEffect(() => {
    if (activeDef) setActive(activeDef.slug);
    else setActive("all");
  }, [paramSlug, activeDef]);

  const select = (slug) => {
    setActive(slug);
    if (slug === "all") setParams({}, { replace: true });
    else setParams({ category: slug }, { replace: true });
  };

  const defsToShow = active === "all" ? PRODUCT_CATEGORY_DEFS : PRODUCT_CATEGORY_DEFS.filter((d) => d.slug === active);

  return (
    <>
      <Seo
        title="Products | STERILTRON Sterilization, Washer-Disinfectors & Consumables"
        description="Explore STERILTRON steam sterilizers, plasma sterilizers, washer-disinfectors and consumables — engineered for validated healthcare and laboratory reprocessing."
        path="/products"
      />
      <PageHero
        label="Products"
        titleLines={["Precision Systems", "Built to Perform"]}
        description="Our portfolio spans medical waste and steam sterilizers, plasma sterilizers, washer-disinfectors, auxiliary equipment and consumables — engineered for validated performance."
      />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-3 mb-16">
            <button
              data-testid="product-filter-all"
              onClick={() => select("all")}
              className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-wider border transition-colors ${active === "all" ? "bg-brand-800 text-white border-brand-800" : "bg-white text-slate-600 border-slate-300 hover:border-brand-800 hover:text-brand-800"}`}
            >
              All
            </button>
            {PRODUCT_CATEGORY_DEFS.map((d) => (
              <button
                key={d.slug}
                data-testid={`product-filter-${d.slug}`}
                onClick={() => select(d.slug)}
                className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-wider border transition-colors ${active === d.slug ? "bg-brand-800 text-white border-brand-800" : "bg-white text-slate-600 border-slate-300 hover:border-brand-800 hover:text-brand-800"}`}
              >
                {d.navLabel}
              </button>
            ))}
          </div>

          <div className="space-y-20 md:space-y-28">
            {defsToShow.map((d) => <CategorySection key={d.slug} def={d} />)}
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
